import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../state/store";

const titleCase = (s: string) =>
  s === "UNNAMED" ? "Unnamed" : s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// ── Ctrl+K global storm search over the full 1851+ track DB ──
// Heals the "2004+ wall" discovery seam: pre-2004 storms were previously only
// reachable via box lists, analog lists, or the compare search buried in the
// Live sidebar. One palette, two actions per storm: Focus / Compare.
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const tracks = useStore((s) => s.tracks);
  const focusStorm = useStore((s) => s.focusStorm);
  const selectCompareStorm = useStore((s) => s.selectCompareStorm);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        setQ("");
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const out: { storm_id: string; name: string; year: number; peak: number }[] = [];
    for (const s of tracks) {
      if (`${s.name} ${s.year}`.toLowerCase().includes(needle)) {
        out.push({
          storm_id: s.storm_id, name: s.name, year: s.year,
          peak: s.track.length ? Math.max(...s.track.map((p) => p.vmax)) : 0,
        });
        if (out.length >= 30) break;
      }
    }
    return out.sort((a, b) => b.year - a.year);
  }, [tracks, q]);

  if (!open) return null;

  const act = (fn: (id: string) => Promise<void>, id: string) => {
    setOpen(false);
    void fn(id);
  };

  return (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 90, background: "rgba(4,6,12,0.6)",
        display: "flex", justifyContent: "center", paddingTop: "12vh",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480, maxHeight: "60vh", display: "flex", flexDirection: "column",
          background: "#0e1424", border: "1px solid rgba(255,255,255,0.16)",
          borderRadius: 12, overflow: "hidden", boxShadow: "0 18px 50px rgba(0,0,0,0.6)",
          height: "fit-content",
        }}
      >
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results.length) act(focusStorm, results[0].storm_id);
          }}
          placeholder="Search any storm, 1851–2025…  (Enter = focus first result)"
          style={{
            padding: "13px 16px", background: "transparent", border: "none", outline: "none",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            color: "white", font: "500 14px Inter, sans-serif",
          }}
        />
        <div style={{ overflowY: "auto" }}>
          {results.map((r) => (
            <div
              key={r.storm_id}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 14px", borderBottom: "1px solid rgba(255,255,255,0.05)",
                font: "400 13px Inter, sans-serif", color: "rgba(255,255,255,0.85)",
              }}
            >
              <span>
                {titleCase(r.name)} <span style={{ color: "rgba(255,255,255,0.45)" }}>({r.year}) · {r.peak} kt</span>
              </span>
              <span style={{ display: "flex", gap: 6 }}>
                <button onClick={() => act(focusStorm, r.storm_id)} style={btn("#60A5FA")}>🎯 Focus</button>
                <button onClick={() => act(selectCompareStorm, r.storm_id)} style={btn("#F59E0B")}>🆚 Compare</button>
              </span>
            </div>
          ))}
          {q.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: 14, color: "rgba(255,255,255,0.45)", font: "400 13px Inter, sans-serif" }}>
              No storms match.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function btn(color: string): React.CSSProperties {
  return {
    padding: "3px 9px", borderRadius: 6, cursor: "pointer",
    border: `1px solid ${color}66`, background: `${color}1f`,
    color: "white", font: "600 11px Inter, sans-serif",
  };
}
